import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { JobStatusUpdateOptions, JobType, UpdateJobInput } from '@clipr/lib';
import { input, InputState } from '../../components/input';
import { assertNotNull } from '../../core';
import { notifyError, notifySuccess } from '../../services/notifications';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { JobModel, JobVideoTypeList, ValidatorResponse } from '../../entities';
import { VideoInformationWindowState } from '../../components/jobs/videoInformationWindowState';
import { ConfirmationModalState } from '../trainerVideoPage/confirmationModalState';
import { AnalyticsEventTypes } from '../../services/analytics/analyticsSchema';

const VideoPrivacyItems = [
  'Shareable',
  'Private'
];

const VideoStatusItems = [
  'Waiting',
  'InReview',
  'Updated',
  'Done'
];

export class TrainerDocumentPageState extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this);

    this.videoPrivacyInput = input(this, {
      name: 'videoPrivacy',
      selectorItems: VideoPrivacyItems,
      onChange: this.handleVideoPrivacyInputChange
    });
    this.videoStatusInput = input(this, {
      name: 'videoStatus',
      selectorItems: VideoStatusItems,
      onChange: this.handleVideoStatusInputChange
    });
    this.videoTypeInput = input(this, {
      name: 'videoType',
      selectorItems: JobVideoTypeList,
      onChange: this.handleVideoTypeInputChange
    });
  }

  @observable jobId: string | null = null;
  @observable isLoading = false;
  @observable error: string | null = null;

  @computed
  get job(): JobModel | null {
    if (!this.jobId)
      return null;
    return this.store.jobManager.maybeGetJob(this.jobId);
  }

  @computed get isJobFailed(): boolean {
    return this.job?.isFailed ?? false;
  }

  @computed get isJobInProgress(): boolean {
    return this.job?.isInProgress ?? false;
  }

  readonly videoPrivacyInput: InputState;
  readonly videoStatusInput: InputState;
  readonly videoTypeInput: InputState;

  async mounted(jobId: string) {
    runInAction(() => {
      this.reset();
      this.isLoading = true;
      this.jobId = jobId;
    });

    const [, err] = await this.store.apiFetchJob(jobId);
    runInAction(() => {
      if (err) {
        this.error = 'Failed to load job.';
        this.isLoading = false;
        return;
      }

      this.videoPrivacyInput.loadValue(this.job?.isPublic ? 'Shareable' : 'Private');
      this.videoStatusInput.loadValue(this.job?.status);
      this.videoTypeInput.loadValue(this.job?.videoType);

      this.isLoading = false;
    });
  }

  @action
  invoke = (type: string) => {
    switch (type) {
      case 'openVideoInformationWindow':
        if (!this.jobId) return;
        const videoInformationWindow = this.store.videoInformationWindow;
        const videoInformationWindowListener = (msg: Message<VideoInformationWindowState>) => {
          switch (msg.type) {
            case 'close':
            case 'jobUpdated':
              videoInformationWindow.unlisten(videoInformationWindowListener);
              this.videoStatusInput.value = this.job?.status || null;
              this.videoTypeInput.value = this.job?.videoType || null;
              break;
          }
        }
        videoInformationWindow.listen(videoInformationWindowListener);
        videoInformationWindow.open({
          jobId: this.jobId
        });
        break;
      case 'openJobPrivacyConfirmationWindow':
        if (!this.jobId) return;
        const confirmationModal = this.store.confirmationModal;
        const confirmationModalListener = (msg: Message<ConfirmationModalState>) => {
          switch (msg.type) {
            case 'close':
              confirmationModal.unlisten(confirmationModalListener);
              this.videoPrivacyInput.value = this.job?.isPublic ? 'Shareable' : 'Private';
              break;
          }
        }
        confirmationModal.listen(confirmationModalListener);
        confirmationModal.open({
          onSubmit: this.updateJobPrivacy,
          modalMessage: `Are you sure you want to make the document ${this.videoPrivacyInput.value}?`,
          title: 'Job Update Confirmation'
        });
        break;
    }
  }

  @action
  unmounted() {
    this.reset();
  }

  @action
  handleVideoStatusInputChange = async () => {
    assertNotNull(this.jobId);
    const { job } = this;

    const value = this.videoStatusInput.value;
    const input: UpdateJobInput = {
      id: this.jobId,
      status: value as JobStatusUpdateOptions
    }

    if (value === 'Done') {
      if (!job)
        return notifyError(this, `Failed to set document status to '${value}'.`);

      const validator: ValidatorResponse | null = job.validateSetToDone();
      const { error } = validator;

      if (error) {
        this.videoStatusInput.value = job.status ?? null;
        return;
      }
    }

    const [, err] = await this.store.jobManager.apiUpdateJob(input);
    if (err) {
      this.videoStatusInput.value = this.job?.status || null;
      return notifyError(this, `Failed to set document status to '${value}'.`);
    }
    notifySuccess(this, `Changed document status to '${value}'.`);
  }

  @action
  handleVideoTypeInputChange = async () => {
    assertNotNull(this.jobId);

    const value = this.videoTypeInput.value;
    const input: UpdateJobInput = {
      id: this.jobId,
      videoType: value as JobType
    }
    const videoTypeLabel = JobVideoTypeList.find(i => i.value === value)?.label;

    const [, err] = await this.store.jobManager.apiUpdateJob(input);
    if (err) {
      this.videoTypeInput.value = this.job?.videoType || null;
      return notifyError(this, `Failed to set content type to '${videoTypeLabel}'.`);
    }
    notifySuccess(this, `Changed content type to '${videoTypeLabel}'.`);
  }

  @action
  handleVideoPrivacyInputChange = async () => {
    this.invoke('openJobPrivacyConfirmationWindow');
  }

  @action
  updateJobPrivacy = async () => {
    assertNotNull(this.jobId);
    const value = this.videoPrivacyInput.value;
    const input: UpdateJobInput = {
      id: this.jobId,
      isPublic: value === 'Shareable'
    }
    const [, err] = await this.store.jobManager.apiUpdateJob(input);
    if (err) {
      this.videoPrivacyInput.value = this.job?.isPublic ? 'Shareable' : 'Private';
      return notifyError(this, `Failed to set document privacy to '${value}'.`);
    }

    if (value === 'Shareable') {
      this.store.analyticsService.registerEvent(
        AnalyticsEventTypes.VideoMakePublicType,
        {
          jobId: this.jobId,
          job: this.job as JobModel
        }
      )
    }

    notifySuccess(this, `Document privacy changed to '${value}'.`);
  }

  @action
  private reset() {
    this.videoTypeInput.reset();
    this.videoStatusInput.reset();
    this.videoPrivacyInput.reset();
    this.jobId = null;
    this.isLoading = false;
    this.error = null;
  }
}
